'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const Relay = require('react-relay/classic');

// Stores
const PriceStore = require('../../../global/stores/PriceStore.es.js');
const ItemStore = require('../../stores/ItemStore.es');
const PdpUiStore = require('../../stores/PdpUiStore.es');
const CalloutStore = require('../../../components/Callout/CalloutStore');
const TourStore = require('../../../components/Tour/TourStore');

// Components
const ContextComponent = require('../../../global/components/ContextComponent');
const { Price: PdpPrice } = require('../../../components/Price/Price');
const PdpFolderItem = require('../Favorites/PdpFolderItem').default;
const PdpFavoriteItem = require('../Favorites/PdpFavoriteItem').default;
const { Connected: Buttons } = require('./Buttons');
const DepositCTA = require('./DepositCTA').default;
const CustomHold = require('./CustomHold');
const PrivateOffer = require('./PrivateOffer');
const ContactPcs = require('../Pcs/ContactPcs').default;
const { Connected: ProductionLeadTime } = require('../Shipping/ProductionLeadTime');
const Blurb = require('../Shipping/Blurb').default;
const { Connected: Title } = require('./Title');
const SaveSearch = require('../SaveSearch/SaveSearch');
const BuyerAssurance = require('../BuyerAssurance/BuyerAssurance');
const SourcingCta = require('../SourcingCta/SourcingCta').default;
const TradeProcurementCta = require('../../../global/components/TradeProcurement/TradeProcurementCta')
    .default;
const ContactDealerProcurementCta = require('../../../global/components/TradeProcurement/ContactDealerProcurementCta')
    .default;
const ContactDealerSidebar = require('../ContactDealer/ContactDealerSidebar');
const AffirmPromo = require('../Affirm/AffirmPromo');
const PromoCode = require('./PromoCode').default;

// Helpers
const provideContext = require('fluxible-addons-react/provideContext');
const connectToStores = require('fluxible-addons-react/connectToStores');
const SV = require('server-vars');
const tokenReplace = require('dibs-token-replace');
const get = require('lodash.get');
const classnames = require('classnames');
const priceHelpers = require('../../helpers/pdpPrice');
const { pageTypes } = require('dibs-buyer-layout/src/entries/index');
const tourTracking = require('../../../global/tracking/tourTracking');
const { type: { TRADE_TOUR } } = TourStore;

const { abExperiments } = require('dibs-buyer-layout/src/entries/index');

const FavoriteModel = require('bunsen/models/identity/Favorite');
const TYPE_SEARCH = FavoriteModel.TYPE_SEARCH;
const FAVORITE_TRACKING = require('../../../components/FavoriteCta/trackingData');

const styles = require('./styles/Sidebar.scss');

// Todo: Price status classes can go away when markup is in Twig as well as React see (see FINDING-7829)
// Add classes to apply min-heights to avoid Sidebar from contracting before all React components mount
const priceStatusToClassMap = {
    [priceHelpers.SOLD]: 'is-sold',
    [priceHelpers.HOLD]: 'is-hold',
    [priceHelpers.UPON_REQUEST]: 'is-upon-request',
    [priceHelpers.SHOW_PRICE]: 'is-show-price',
};

/**
 * quick and dirty to stop using pdpPrice module logic (still using constants)
 * @param isSold
 * @param isOnHold
 * @param isHidingPrice
 */
const getStatus = ({ isSold, isOnHold, showPriceUponRequest }) => {
    if (isSold) {
        return priceHelpers.SOLD;
    }
    if (isSold || isOnHold) {
        // On Hold, Recently Sold (i.e. not using sold template) flow
        return priceHelpers.HOLD;
    }
    if (showPriceUponRequest) {
        return priceHelpers.UPON_REQUEST;
    }
    return priceHelpers.SHOW_PRICE;
};

class Sidebar extends ContextComponent {
    constructor(props) {
        super(props);

        this.state = {
            isUrgencyVisible: false,
        };

        this._contactDealer = this._contactDealer.bind(this);
    }

    componentDidMount() {
        const { pdpUrgency } = abExperiments;
        if (pdpUrgency) {
            pdpUrgency.then(() => {
                this.setState({ isUrgencyVisible: true });
            });
        }
    }

    _contactDealer(e) {
        e.preventDefault();
        this.props.contactDealerClick(this.props);
    }

    render() {
        const props = this.props;
        const {
            copy = {},
            currency,
            customHold,
            dealerPk,
            isAsyncItemLoaded,
            isOnHold,
            isPreview,
            isSold,
            isUnlisted,
            itemId,
            priceDisplays,
            context,
            quotes,
            user = null,
            hasUserId,
            userId,
            userIds,
            viewer,
            relayItem,
            favoriteDealer,
            favoriteDealerCta,
            dealerData,
            showReducedFrom,
            callout,
            lastCallout,
            selectedItemIds,
            loadPortfolioData,
            shouldFillInFolder,
            forceFavoriteItem,
            onFavorited,
            onAddItemSuccess,
            isRemoveContactDealerCTATestRunning,
        } = props;
        const relationshipData = get(this.props, 'viewer.userRelationships[0].userDataB[0]');
        const showVipContact = user && !user.isVerifiedTrade && !!relationshipData;
        const priceStates = copy.priceStates;
        const priceStatus = getStatus({
            isSold,
            isOnHold,
            showPriceUponRequest: priceDisplays.showPriceUponRequest,
        });
        const expirationDate =
            priceDisplays.showOnHoldCustom && (customHold && customHold.expirationDate)
                ? customHold.expirationDate
                : null;
        const isVerifiedTrade = get(user, 'isVerifiedTrade');
        const userLoaded = !hasUserId || (hasUserId && !!get(user, 'serviceId'));
        const hasRequestedHoldForBuyer = get(
            viewer,
            'buyerItemConversation.hasRequestedHoldForBuyer'
        );
        const hasActiveHoldForBuyer = get(viewer, 'buyerItemConversation.hasActiveHoldForBuyer');
        const isOnHoldForBuyer = hasRequestedHoldForBuyer || hasActiveHoldForBuyer;
        const itemEligibleForProcurement =
            get(relayItem, 'isFurniture') || get(relayItem, 'isArt') || false;

        let buttonsEl;
        let requestInfoEl;
        let priceEl;
        let middleSectionClassName;
        // Save Search
        const saveName = { name: SV.get('saveSearchData.name') };
        const favoriteData = {
            name: saveName.name,
            page: SV.get('saveSearchData.uri'),
            type: TYPE_SEARCH,
            authModalOptions: FAVORITE_TRACKING.SEARCH.SOLD_PDP_AUTH_MODAL,
        };
        // Relay variables for Heart & Folder icon queries/fragments
        const pdpFavoriteItemRelayVariables = {
            loadPortfolioData,
            selectedItemIds,
            userId,
            userIds,
        };

        const saveSearchProps = {
            context: context,
            description: tokenReplace(SV.get('favoriteCta.soldPdp.description'), saveName),
            cta: SV.get('favoriteCta.soldPdp.cta'),
            successMessage: tokenReplace(
                SV.get('favoriteCta.soldPdp.confirmationTooltip'),
                saveName
            ),
            favoriteData: favoriteData,
            tracking: FAVORITE_TRACKING.SEARCH.SOLD_PDP,
        };

        // FINDING-8230 Wait on displaying price to ensure converted currencies reflect latest conversion rates
        if (isAsyncItemLoaded || isPreview) {
            priceEl = (
                <PdpPrice
                    context={context}
                    pageType={pageTypes.PAGE_TYPE.PDP}
                    showReducedFrom={showReducedFrom}
                    showTour
                />
            );
        }

        const saveSearchContainerClassName = classnames(styles.saveSearchContainer, {
            [styles.onHold]: isOnHold,
        });
        const SourcingCtaButtonsEl = (
            <div className={styles.flexButtons}>
                <SourcingCta user={user} item={relayItem} />
                <div className={saveSearchContainerClassName}>
                    <SaveSearch {...saveSearchProps} isSmall />
                </div>
            </div>
        );

        // Show Sourcing CTA + Save search for trade users if sold, unlisted or on hold item
        if ((isSold || isUnlisted || (isOnHold && !isOnHoldForBuyer)) && isVerifiedTrade) {
            middleSectionClassName = styles.middleSectionClassName;
            buttonsEl = SourcingCtaButtonsEl;
            // Show Save Search if sold item
        } else if (isSold || (isUnlisted && userLoaded)) {
            buttonsEl = <SaveSearch {...saveSearchProps} />;
            // Show shipping blurb if non-sold item is on hold (but could be on hold for a user) and is not sold
        } else if (!priceDisplays.showOnHold) {
            buttonsEl = (
                <Buttons
                    context={context}
                    user={user}
                    itemEligibleForProcurement={itemEligibleForProcurement}
                />
            );
        }

        if (relayItem) {
            if (isRemoveContactDealerCTATestRunning) {
                requestInfoEl = (
                    <ContactDealerProcurementCta item={relayItem} copy={copy.sidebar} />
                );
            } else if (!isSold && !showVipContact) {
                requestInfoEl = (
                    <ContactDealerSidebar
                        context={context}
                        onClick={this._contactDealer}
                        copy={copy.sidebar}
                        itemEligibleForProcurement={itemEligibleForProcurement}
                        isCustomizable={relayItem.isCustomizable}
                        user={user}
                    />
                );
            }
        }

        const priceClasses = classnames('pdp-price-container', {
            'has-net-price': priceDisplays.showNetPrice,
            'has-sale-price': priceDisplays.showSalePrice,
            'has-unavailable-price': priceDisplays.showSold || isUnlisted,
        });

        const showBuyerAssurance = (hasUserId && user) || !hasUserId;

        return (
            <div className={`pdp-sidebar ${priceStatusToClassMap[priceStatus]}`}>
                <div className="pdp-sidebar-box">
                    <div className="pdp-sidebar-top-section">
                        <div className="pdp-sidebar-top-favorite-item">
                            <PdpFavoriteItem
                                userId={userId}
                                itemId={itemId}
                                viewer={viewer}
                                item={relayItem}
                                callout={callout}
                                lastCallout={lastCallout}
                                forceFavoriteItem={forceFavoriteItem}
                                onFavorited={onFavorited}
                                {...pdpFavoriteItemRelayVariables}
                            />
                            <PdpFolderItem
                                itemId={itemId}
                                viewer={viewer}
                                item={relayItem}
                                callout={callout}
                                shouldFillInFolder={shouldFillInFolder}
                                onAddItemSuccess={onAddItemSuccess}
                                {...pdpFavoriteItemRelayVariables}
                            />
                        </div>
                        <Title context={context} />
                        <div className={priceClasses}>
                            {priceDisplays.showOnHold && (
                                <div className="is-on-hold">{priceStates.onHold}</div>
                            )}
                            {priceDisplays.showOnHoldCustom ? (
                                <CustomHold
                                    customHoldCopy={priceStates.onHoldForUser}
                                    expirationDate={expirationDate}
                                />
                            ) : null}
                            {priceEl}
                            <PrivateOffer
                                conversation={get(viewer, 'buyerItemConversation', null)}
                                currency={currency}
                                pdpBundleEnabled={get(viewer, 'pdpBundleEnabled')}
                            />
                            <PromoCode
                                userId={userId}
                                itemId={itemId}
                                showNetPrice={priceDisplays.showNetPrice}
                                showSalePrice={priceDisplays.showSalePrice}
                            />
                        </div>
                    </div>
                    <div className={`pdp-sidebar-middle-section ${middleSectionClassName}`}>
                        {buttonsEl}
                    </div>
                    <DepositCTA userId={userId} item={relayItem} />
                    {!isSold && !isUnlisted ? (
                        <AffirmPromo viewer={viewer} user={user} item={relayItem} />
                    ) : null}
                    {itemEligibleForProcurement && (
                        <TradeProcurementCta
                            user={user}
                            item={relayItem}
                            hasUserId={hasUserId}
                            userId={userId}
                            className={styles.tradeProcurementCta}
                        />
                    )}
                    {!isSold && !isUnlisted ? (
                        <div className="pdp-sidebar-bottom-section">
                            {showVipContact && (
                                <ContactPcs
                                    email={relationshipData.email}
                                    name={relationshipData.firstName}
                                    phoneNumber={relationshipData.phoneNumber}
                                />
                            )}
                            <ProductionLeadTime relayItem={relayItem} />
                            <Blurb
                                hasUserId={hasUserId}
                                userId={userId}
                                itemId={itemId}
                                dealerPk={dealerPk}
                                viewer={viewer}
                                context={context}
                                relayItem={relayItem}
                                quotes={quotes}
                                user={user}
                                favoriteDealer={favoriteDealer}
                                favoriteDealerCta={favoriteDealerCta}
                                dealerData={dealerData}
                                conversation={get(viewer, 'buyerItemConversation', null)}
                            />
                            {showBuyerAssurance && <BuyerAssurance user={user} item={relayItem} />}
                        </div>
                    ) : null}
                </div>
                <div className="pdp-print-hidden">{requestInfoEl}</div>
            </div>
        );
    }
}

Sidebar.defaultProps = {
    showReducedFrom: true,
};

Sidebar.propTypes = {
    buyerAssuranceProps: PropTypes.shape({
        copy: PropTypes.object.isRequired,
        state: PropTypes.shape({
            showInfo: PropTypes.bool.isRequired,
        }),
    }),
    contactDealerClick: PropTypes.func.isRequired,
    context: PropTypes.object,
    copy: PropTypes.object.isRequired,
    customHold: PropTypes.object,
    dealerPk: PropTypes.string.isRequired,
    hidePrice: PropTypes.bool.isRequired,
    isAsyncItemLoaded: PropTypes.bool,
    isOnHold: PropTypes.bool.isRequired,
    isPreview: PropTypes.bool,
    isSold: PropTypes.bool.isRequired,
    isUnlisted: PropTypes.bool.isRequired,
    relayItem: PropTypes.object,
    itemId: PropTypes.string.isRequired,
    priceDisplays: PropTypes.object.isRequired,
    showReducedFrom: PropTypes.bool,
    tradeTour: PropTypes.object,
    userId: PropTypes.string,
    viewer: PropTypes.object,
    favoriteDealer: PropTypes.object,
    favoriteDealerCta: PropTypes.object,
    dealerData: PropTypes.object,
    callout: PropTypes.object,
    lastCallout: PropTypes.string,
    forceFavoriteItem: PropTypes.bool,
    vertical: PropTypes.string,
    isRemoveContactDealerCTATestRunning: PropTypes.bool,
};

module.exports = Relay.createContainer(
    provideContext(
        connectToStores(
            Sidebar,
            [ItemStore, PriceStore, PdpUiStore, TourStore, CalloutStore],
            context => {
                const calloutStore = context.getStore(CalloutStore);
                const itemStore = context.getStore(ItemStore);
                const priceStore = context.getStore(PriceStore);
                const uiStore = context.getStore(PdpUiStore);
                const tourStore = context.getStore(TourStore);
                const item = itemStore.getItem();
                const processedPriceData = priceStore.processPrice(item);

                return {
                    buyerAssuranceProps: uiStore.getBuyerAssuranceProps(),
                    copy: itemStore.getCopy(),
                    customHold: itemStore.getFromItemModel('activeHold'),
                    dealerPk: itemStore.getFromSV('data.dealerPk'),
                    hidePrice: itemStore.getHidePrice(),
                    isAsyncItemLoaded: itemStore.getIsAsyncItemLoaded(),
                    isOnHold: itemStore.getIsOnHold(),
                    isPreview: itemStore.getIsPreview(),
                    isSold: itemStore.getIsSold(),
                    isUnlisted: itemStore.getIsUnlisted(),
                    itemId: itemStore.getFromSV('data.pk'),
                    priceDisplays: processedPriceData.priceDisplays,
                    tradeTour: {
                        type: TRADE_TOUR,
                        subType: processedPriceData.priceDisplays.showNetPrice
                            ? 'preQuotedNetPrice'
                            : 'requestNetPrice',
                        copy: SV.get(`tourCopy.${TRADE_TOUR}`),
                        isTourOpen: tourStore.isTourOpen(TRADE_TOUR),
                        currentStep: tourStore.getCurrentStep(TRADE_TOUR),
                        totalSteps: tourStore.getTotalSteps(TRADE_TOUR),
                        onOpen: tourTracking.trackOpen,
                        onClose: tourTracking.trackClose,
                    },
                    callout: calloutStore.getCalloutData(),
                    lastCallout: calloutStore.getLastCallout(),
                    forceFavoriteItem: uiStore.getForceFavoriteItem(),
                };
            }
        )
    ),
    {
        initialVariables: {
            userId: null,
            userIds: [],
            itemId: null,
            hasUserId: false,
            selectedItemIds: [],
            shouldFillInFolder: false,
            loadPortfolioData: false,
        },
        fragments: {
            user: ({ itemId, userId, hasUserId }) => Relay.QL`
            fragment on User {
                ${TradeProcurementCta.getFragment('user', { hasUserId, userId })}
                ${Blurb.getFragment('user', { hasUserId, itemId, userId })}
                ${Buttons.getFragment('user')}
                ${SourcingCta.getFragment('user')}
                ${ContactDealerSidebar.getFragment('user')}
                ${AffirmPromo.getFragment('user')}
                ${BuyerAssurance.getFragment('user')}
                serviceId
                isVerifiedTrade
            }
        `,
            viewer: ({
                itemId,
                userId,
                userIds,
                hasUserId,
                selectedItemIds,
                shouldFillInFolder,
                loadPortfolioData,
            }) => Relay.QL`
            fragment on Viewer {
                ${PdpFavoriteItem.getFragment('viewer', {
                    itemId,
                    loadPortfolioData,
                    selectedItemIds,
                    userId,
                    userIds,
                })}
                ${PdpFolderItem.getFragment('viewer', {
                    itemId,
                    loadPortfolioData,
                    selectedItemIds,
                    shouldFillInFolder,
                    userId,
                    userIds,
                })}
                ${Blurb.getFragment('viewer', { hasUserId, itemId, userId })}
                ${AffirmPromo.getFragment('viewer')}
                buyerItemConversation (buyerId: $userId, itemId: $itemId) @include(if: $hasUserId) {
                    hasRequestedHoldForBuyer
                    hasActiveHoldForBuyer
                    ${PrivateOffer.getFragment('conversation')}
                    ${Blurb.getFragment('conversation')}
                }
                userRelationships(userIds: $userIds, relationshipType: VIP) @include(if: $hasUserId) {
                    userDataB {
                        ... on VipRepData {
                            email
                            firstName
                            phoneNumber(formatType: INTERNATIONAL)
                        }
                    }
                }
                pdpBundleEnabled: isEnabled(key: "pdpBundle")
            }
        `,
            relayItem: ({
                itemId,
                userId,
                userIds,
                hasUserId,
                selectedItemIds,
                shouldFillInFolder,
                loadPortfolioData,
            }) => Relay.QL`
            fragment on Item {
                ${TradeProcurementCta.getFragment('item', { hasUserId, userId })}
                ${ContactDealerProcurementCta.getFragment('item')}
                ${Blurb.getFragment('relayItem', { hasUserId, itemId, userId })}
                ${PdpFavoriteItem.getFragment('item', {
                    itemId,
                    loadPortfolioData,
                    selectedItemIds,
                    userId,
                    userIds,
                })}
                ${PdpFolderItem.getFragment('item', {
                    itemId,
                    loadPortfolioData,
                    selectedItemIds,
                    shouldFillInFolder,
                    userId,
                    userIds,
                })}
                ${ProductionLeadTime.getFragment('relayItem')}
                ${SourcingCta.getFragment('item')}
                ${AffirmPromo.getFragment('item')}
                ${DepositCTA.getFragment('item', { userId })}
                ${BuyerAssurance.getFragment('item')}
                isFurniture
                isArt
                isCustomizable
                listingScore
            }
        `,
        },
    }
);



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/Sidebar/Sidebar.jsx